import { useEffect, useState } from 'react';
import {
    message,
    PageHeader,
    Table,
    Tag,
    Input,
    TablePaginationConfig,
    Select,
    Button,
    Dropdown,
    Menu,
} from 'antd';
import { Helmet } from 'react-helmet';

import ClientsService from './ClientsService';
import styles from './clientslistpage.module.css';
import DateDisplay from '../../components/Date';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    DeleteOutlined,
    EditOutlined,
    EllipsisOutlined,
    ExclamationCircleOutlined,
    FolderOpenOutlined,
    TeamOutlined,
} from '@ant-design/icons';
import confirm from 'antd/lib/modal/confirm';

const PAGE_SIZE = 20;

const columns = [
    { title: 'Nombre', dataIndex: 'name' },
    {
        title: 'Creado',
        dataIndex: 'created_at',
        className: styles.stretchCol,
        width: 100,
        render: (val: number) => (
            <DateDisplay relative={true}>{val}</DateDisplay>
        ),
    },
    {
        title: 'Estado',
        dataIndex: 'archived',
        className: styles.stretchCol,
        width: 100,
        render: (val: number) =>
            val ? (
                <Tag color="warning">Archivado</Tag>
            ) : (
                <Tag color="success">Activo</Tag>
            ),
    },
];

const getModule = () =>
    window.location.pathname.indexOf('/cliente-oculto') >= 0
        ? 'clio'
        : 'quarter';

const ClientsListPage = () => {
    const title = 'Clientes';

    const location = useLocation();
    const navigate = useNavigate();

    const [clients, setClients] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState('');
    const [archived, setArchived] = useState('false');
    const [pagination, setPagination] = useState<TablePaginationConfig>({
        current: 1,
        pageSize: PAGE_SIZE,
        total: 0,
    });
    const [page, setPage] = useState(pagination.current || 1);
    const [lastUpdate, setLastUpdate] = useState<Date>();

    const onQueryChanged = (value: string) => {
        setQuery(value);
        setPagination((pag) => ({ ...pag, current: 1 }));
    };

    const onFilterChanged = (value: string) => {
        setArchived(value);
        setPagination((pag) => ({ ...pag, current: 1 }));
    };

    const handleTableChange = (pagination: TablePaginationConfig) => {
        setPagination((pag) => ({ ...pag, ...pagination }));
    };

    const onRowClick = (r: any) => {
        navigate(`/admin/evals/${r.slug}/surveys`);
    };

    const handleRowAction = async (key: React.Key, client: any) => {
        try {
            switch (key) {
                case 'edit':
                    navigate(`/admin/clients/${client.slug}/edit`, {
                        state: { module: getModule() },
                    });
                    break;
                case 'users':
                    navigate(`/admin/clients/${client.slug}/users`, {
                        state: { module: getModule() },
                    });
                    break;
                case 'archive':
                    await ClientsService.archiveClient(
                        client.slug,
                        !client.archived
                    );
                    setLastUpdate(new Date());
                    message.success(
                        client.archived
                            ? 'El cliente fue restaurado'
                            : 'El cliente fue archivado'
                    );
                    break;
                case 'delete':
                    confirm({
                        title: '¿Estás seguro de que quieres eliminar este cliente?',
                        icon: <ExclamationCircleOutlined />,
                        content:
                            'Sólo se puede eliminar el cliente si no tiene contenido. Esta acción no se puede deshacer',
                        onOk: async () => {
                            try {
                                await ClientsService.deleteClient(client.slug);
                                setLastUpdate(new Date());
                                message.success('El cliente fue eliminado');
                            } catch (err) {
                                message.error((err as Error).message);
                            }
                        },
                    });
                    break;
            }
        } catch (err) {
            message.error((err as Error).message);
        }
    };

    useEffect(() => {
        setPage(pagination.current || 1);
    }, [pagination]);

    useEffect(() => {
        const queryClients = async () => {
            try {
                setLoading(true);
                const data = await ClientsService.getClients(
                    query,
                    archived,
                    getModule(),
                    page,
                    PAGE_SIZE
                );
                setClients(data.results);
                setPagination((pag) => ({
                    ...pag,
                    total: data.metadata.total,
                }));
            } catch (err) {
                message.error((err as Error).message);
            }

            setLoading(false);
        };
        queryClients();
    }, [location, query, page, archived, lastUpdate]);

    return (
        <>
            <Helmet>
                <title>{title}</title>
            </Helmet>
            <PageHeader
                onBack={() => navigate('/')}
                title={title}
                extra={[
                    <Button
                        onClick={() =>
                            navigate('/admin/clients/new', {
                                state: { module: getModule() },
                            })
                        }
                        type="primary"
                        key="1"
                    >
                        Registrar cliente
                    </Button>,
                ]}
            ></PageHeader>
            <div className={styles.filters}>
                <Input.Search
                    placeholder="Buscar cliente (ej. 'Cliente oculto')"
                    onSearch={onQueryChanged}
                    style={{ width: 350 }}
                />
                <Select
                    style={{ width: 250 }}
                    defaultValue="false"
                    onChange={onFilterChanged}
                >
                    <Select.Option value="">Mostrar: Todos</Select.Option>
                    <Select.Option value="false">
                        Mostrar: Solo activos
                    </Select.Option>
                    <Select.Option value="true">
                        Mostrar: Solo archivados
                    </Select.Option>
                </Select>
            </div>
            <Table
                className={styles.table}
                rowKey="id"
                loading={loading}
                dataSource={clients}
                columns={[
                    ...columns,
                    {
                        title: '',
                        width: 40,
                        render: (_val: any, record: any) => (
                            <div onClick={(e) => e.stopPropagation()}>
                                <Dropdown
                                    overlay={
                                        <Menu
                                            onClick={({ key }) =>
                                                handleRowAction(key, record)
                                            }
                                        >
                                            <Menu.Item key="edit">
                                                <EditOutlined /> Editar
                                            </Menu.Item>
                                            <Menu.Item key="users">
                                                <TeamOutlined /> Usuarios
                                            </Menu.Item>
                                            <Menu.Divider />
                                            <Menu.Item key="archive">
                                                <FolderOpenOutlined />{' '}
                                                {record.archived
                                                    ? 'Restaurar'
                                                    : 'Archivar'}
                                            </Menu.Item>
                                            <Menu.Item key="delete">
                                                <DeleteOutlined /> Eliminar
                                            </Menu.Item>
                                        </Menu>
                                    }
                                    trigger={['click']}
                                >
                                    <Button type="link">
                                        <EllipsisOutlined />
                                    </Button>
                                </Dropdown>
                            </div>
                        ),
                    },
                ]}
                pagination={{ hideOnSinglePage: true, ...pagination }}
                onChange={handleTableChange}
                onRow={(r) => ({ onClick: () => onRowClick(r) })}
            />
        </>
    );
};

export default ClientsListPage;
